import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';

// Prime React Components
import { classNames } from 'primereact/utils';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';

// Needed URLS
import { GET_NEURELIS_SEARCHES, ADD_NEURELIS_SEARCH, EDIT_NEURELIS_SEARCH, DELETE_NEURELIS_SEARCH} from '../constants/URLS';
const GET_ENTITY = GET_NEURELIS_SEARCHES
const ADD_ENTITY = ADD_NEURELIS_SEARCH
const EDIT_ENTITY = EDIT_NEURELIS_SEARCH
const DELETE_ENTITY = DELETE_NEURELIS_SEARCH

// Needed Components
const formData = new FormData();

const Searches = (props) => {

	// PAGE REFS
	const toast = useRef(null);
    const dt = useRef(null);
	const fileUploadRef = useRef(null);

	// CONSTANTS
    const emptyEntity = {
        searchID: null,
        title: '',
		url: '',
		content: '',
		category: '',
		locked: 0,
		document_type: 'page'
    };


	// STATE VARIABLES
    const [ entities, setEntities ] = useState([]); // holds all the existing 
	const [ entity, setEntity ] = useState(emptyEntity); // current entity that we are adding or editing
	const [ entityDialog, setEntityDialog ] = useState(false);
    const [ deleteEntityDialog, setDeleteEntityDialog ] = useState(false);
    const [ submitted, setSubmitted ] = useState(false);
    const [ globalFilterValue1, setGlobalFilterValue1 ] = useState('');
	const [ filters, setFilters ] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS }
		
    });

	// USE EFFECTS HOOKS
    useEffect(() => {
		fncGet()
    }, []); 

	// ACTION / XHR FUNCTIONS
	const fncGet = async () => {
		let strURL = GET_ENTITY
		let entityXHR = await axios({method: 'get', url: strURL})
		// need to do some slight data modifications for the form

		if (entityXHR.data.success) {
			setEntities(entityXHR.data.results.map(cur => {
				return {
					...cur
				}
			}))
		} else {
			toast.current.show({ severity: 'error', summary: 'Error', detail: entityXHR.data.message, life: 3000 });
		}
	};

	const fncSave = async () => {
        setSubmitted(true);
		let errors = []
		if (!entity.title.trim()) errors.push('title')
		

		if (errors.length > 0) {
			return  toast.current.show({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.', life: 3000 });
		}

		
		let _entity = {...entity};
		const formData = new FormData();
		formData.append("title", _entity.title);
		formData.append("url", _entity.url);
		formData.append("content", _entity.content);
		formData.append("category", _entity.category);
		formData.append("locked", _entity.locked);
		formData.append("document_type", _entity.document_type);

		if (_entity.searchID) {
			formData.append("searchID", _entity.searchID);
			let editResults = await axios({
				method: 'post', 
				url: EDIT_ENTITY,
				data: formData
			})

			if (editResults.data.success) {
				//give the API a little time to index the new entry
				setTimeout(() => {
					fncGet()
				}, 1000);
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Search Index Edited', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: editResults.data.message, life: 3000 });
			}
		} else {
			
			let addResults = await axios({
				method: 'post', 
				url: ADD_ENTITY,
				data: formData
			})

			if (addResults.data.success) {
				//give the API a little time to index the new entry
				setTimeout(() => {
					fncGet()
				}, 1000);
				
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Search Index Created', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: addResults.data.message, life: 3000 });
			}
		}

		setSubmitted(false);
		setEntityDialog(false);
		clearEntity()
	}

	const fncDelete = async () => { 
		try {
			let deleteResults = await axios({
				method: 'post', 
				url: DELETE_ENTITY,
				data: {...entity}
			})
			if (deleteResults.data.success) {
				let _entities = entities.filter(val => val.searchID !== entity.searchID);
				setEntities(_entities);
				setDeleteEntityDialog(false);
				clearEntity()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Search Index Deleted', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: deleteResults.data.message, life: 3000 });
			}
		} catch (err) {
			toast.current.show({ severity: 'error', summary: 'Error', detail: 'There was an error deleting.', life: 3000 });
			console.error(err)
		}
	}
	
	
	// OTHER FUNCTIONS
	const clearEntity = () => {
		setEntity(emptyEntity); 
	}
	
	const openNew = () => {
		clearEntity()
		setSubmitted(false);
		setEntityDialog(true);
	}

	const hideDialog = () => {
        setSubmitted(false);
        setEntityDialog(false);
    }

	const onGlobalFilterChange1 = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue1(value);
    }

	const hideDeleteEntityDialog = () => {
        setDeleteEntityDialog(false);
    }

	const onInputChange = (value, name) => {
        let _entity = {...entity};
        _entity[`${name}`] = value;
        setEntity(_entity);
    }

	const fncEdit = (entity) => {
		setEntity(entity);
        setEntityDialog(true);
    }

	const confirmDeleteEntity = (entity) => {
        setEntity(entity);
        setDeleteEntityDialog(true);
    }

	// TABLE COLUMN TEMPLATES (table provides rowdata and column info, can draw how needed)
	const actionBodyTemplate = (rowData) => {
		return (
			<>
				<Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" onClick={() => fncEdit(rowData)} />
				<Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDeleteEntity(rowData)} />
			</>
		);
    }

	const dateTemplate = (rowData, column) => {
        
		return (
			<div className="flex flex-column">
				<div >
					{dayjs(rowData.timeStamp).format('MMM D, YYYY')}
				</div>
				<div >
					{dayjs(rowData.timeStamp).format('h:mm A')}
				</div>
			</div>
		);
    }

	const titleTemplate = (rowData) => {
		return (
			<div className="flex eventCard">
				
				<div className="flex flex-column">
					<div className="title">
						{rowData.title}
					</div>
				</div>
			</div>
		)
	}

	const contentTemplate = (rowData) => {
		return (
			<div class="overflow-hidden text-overflow-ellipsis" style={{"paddingBottom": "20px","maxHeight":"200px"}}>
				{rowData.content}
			</div>
		)
	}


	// OTHER TEMPLATES (JSX)
	const leftToolbarTemplate = () => {
        return <Button label="Add New Search Index" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
    }

	const rightToolbarTemplate = () => {
        return null
    }
	
	const header = (
        <div className="table-header flex justify-content-between" >
            <h5 className="mx-0 my-1" >Manage Search Indexes</h5>
			<div className="flex flex-column">
				<span className="p-input-icon-left">
					<i className="pi pi-search" />
					<InputText value={globalFilterValue1} onChange={onGlobalFilterChange1} placeholder="Search..." />
				</span>
			</div>
        </div>
    );

	const entityDialogFooter = () => {
		
		if (entity.document_type === 'page') {
			return (
					<>
					<Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
					<Button label="Save" icon="pi pi-check" className="p-button-text" onClick={fncSave} />
				</>
			)
		} else {
			return (
				<div className="flex justify-content-between">
					<div>
						Edits are managed automatically by the item.
					</div>
					<Button label="Close" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
				</div>
			)
		}
	};

	

    const deleteEntityDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteEntityDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={fncDelete} />
        </>
    );


	

	 return (
        <div className="datatable-resources">
             <Toast ref={toast} />

			<div className="card" style={{width:"100%"}}>
				<Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

				<DataTable ref={dt} value={entities} sortField="startDate" sortOrder={-1}  
					dataKey="eventID" paginator rows={10} rowsPerPageOptions={[5, 10, 25]} paginatorPosition="both"
					paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
					currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Searches"
					globalFilterFields={['title', 'category', 'subcategory', 'content']}
					filters={filters} header={header}>
						<Column field="timeStamp" header="Time Stamp" body={dateTemplate} sortable style={{ width: '10%', verticalAlign:'top' }}></Column>
						<Column field="document_type" header="Doc Type" sortable style={{ width: '10%', verticalAlign:'top' }}></Column>
						<Column field="title" header="Title" body={titleTemplate} sortable style={{ width: '15%', verticalAlign:'top' }}></Column>
						<Column field="category" header="Category" style={{ width: '10%', verticalAlign:'top' }}></Column>
						<Column field="subcategory" header="Subcategory" style={{ width: '10%', verticalAlign:'top' }}></Column>
						<Column field="url" header="URL" style={{ width: '15%', verticalAlign:'top' }}></Column>
						<Column field="content" header="Content" body={contentTemplate} style={{ width: '20%', verticalAlign:'top' }}></Column>
						<Column body={actionBodyTemplate} exportable={false} style={{ width: '10%', verticalAlign:'top' }}></Column>
				</DataTable>
            </div>

			<Dialog visible={entityDialog} style={{ width: '850px' }} header="Search Index Details" modal className="p-fluid" footer={entityDialogFooter} onHide={hideDialog}>
                <div className="flex flex-column inputForm">
					<div className="inputFormFields">
						<div className="flex">
							<div className="field fullField" >
								<label htmlFor="title">Search Title</label>
								<InputText id="title" value={entity.title} onChange={(e) => onInputChange(e.target.value, 'title')} required className={classNames({ 'p-invalid': submitted && !entity.title })} />
								{submitted && !entity.title && <small className="p-error">Title is required.</small>}
							</div>
						</div>


						<div className="flex">
							<div className="field fullField" >
								<label htmlFor="url">Search URL</label>
								<InputText id="url" value={entity.url} onChange={(e) => onInputChange(e.target.value, 'url')} required className={classNames({ 'p-invalid': submitted && !entity.url })} />
								{submitted && !entity.url && <small className="p-error">URL is required.</small>}
							</div>
						</div>

						<div className="flex">
							<div className="field fullField" >
								<label htmlFor="category">Search Category</label>
								<InputText id="category" value={entity.category} onChange={(e) => onInputChange(e.target.value, 'category')} required className={classNames({ 'p-invalid': submitted && !entity.category })} />
								{submitted && !entity.category && <small className="p-error">Category is required.</small>}
							</div>
						</div>

						<div className="flex">
							<div className="field fullField" >
								<label htmlFor="content">Content</label>
								<InputTextarea id="content" style={{height:'120px'}} value={entity.content} onChange={(e) => onInputChange(e.target.value, 'content')} />
								{submitted && !entity.category && <small className="p-error">Content is required.</small>}
							</div>
						</div>

					</div>
				</div>
            </Dialog>
			<Dialog visible={deleteEntityDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteEntityDialogFooter} onHide={hideDeleteEntityDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem'}} />
                    {entity && <span>Are you sure you want to delete <b>{entity.title}</b>?</span>}
                </div>
            </Dialog>
        </div>
    );
}

export default Searches;